/*
 * Copyright (c) Nexign, JSC, 2019-2023.
 */
/**
 *
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @author Nikolay Sviridov
 * @version 1.38.0
 * @since 1.0.0
 */

import { memo, RefObject } from 'react';
import type { FC, MouseEvent, KeyboardEvent, ReactNode } from 'react';

import invoke from 'lodash/invoke';
import { Link } from 'react-router-dom';

import { icons } from '@/main/assets';
import type { TLinkTarget } from '@/main/types/globalTypes';
import { KeyboardKeyEnum } from '@modules/app/appConstants';

import { ImgStyled } from '../Block/Img';
import { LinkArrowContainer, LinkContainerStyled, LinkTextBlock } from './styled/LinkStyled';
import { splitUrl } from './utils/splitUrl';

export interface ILinkProps {
    children: ReactNode | string;
    withArrow?: boolean;
    className?: string;
    dataName?: string;
    disabled?: boolean;
    inner?: boolean;
    isExternalUrl?: boolean;
    linkState?: Record<string, string | boolean>;
    onClick?: (e: MouseEvent) => void;
    onWheelClick?: (e: MouseEvent) => void;
    target?: TLinkTarget;
    tabIndex?: number;
    url?: string;
    linkRef?: RefObject<HTMLAnchorElement>;
}

export const linkBlockClassName = 'link-block__link';

const LinkBlock: FC<ILinkProps> = (props) => {
    const {
        withArrow,
        className,
        children,
        dataName,
        disabled,
        inner,
        isExternalUrl,
        linkState,
        tabIndex,
        target,
        url,
        onClick,
        onWheelClick,
        linkRef,
    } = props;

    const keyDownHandler = (event: KeyboardEvent): void => {
        if (event.key === KeyboardKeyEnum.ENTER && !disabled) {
            event.stopPropagation();
            invoke(props, 'onClick');
        }
    };
    const clickHandler = (event: MouseEvent): void => {
        if (event.button === 0 && onClick && !disabled) {
            event.preventDefault();
            onClick(event);
        }
    };
    const mouseUpClickHandler = (event: MouseEvent): void => {
        if (event.button === 1 && onWheelClick && !disabled) {
            //wheel click event
            event.preventDefault();
            onWheelClick(event);
        }
    };

    const [path, params] = splitUrl(url || '');

    const LinkArrow = withArrow ? (
        <LinkArrowContainer>
            <ImgStyled height="10px" src={icons.arrowRightInfoSmall} width="6px" />
        </LinkArrowContainer>
    ) : null;

    return (
        <LinkContainerStyled
            className={className}
            data-link={dataName}
            data-name={dataName}
            disabled={disabled}
            inner={inner}
            tabIndex={tabIndex}
            onClick={clickHandler}
            onKeyDown={keyDownHandler}
            onMouseDown={mouseUpClickHandler}
        >
            <LinkTextBlock>
                {url ? (
                    isExternalUrl ? (
                        <a className={linkBlockClassName} href={url} ref={linkRef} target={target || '_self'}>
                            {children}
                        </a>
                    ) : (
                        <Link
                            className={linkBlockClassName}
                            ref={linkRef}
                            to={{
                                pathname: path,
                                state: linkState,
                                search: params,
                            }}
                        >
                            {children}
                        </Link>
                    )
                ) : (
                    children
                )}
                {LinkArrow}
            </LinkTextBlock>
        </LinkContainerStyled>
    );
};
LinkBlock.defaultProps = {
    className: '',
    url: '',
};

export default memo(LinkBlock);
